import { Chart, ChartData, ChartOptions, registerables } from "chart.js";
import { cloneDeep, isEqual, merge } from "lodash-es";
import * as React from "react";
import BrowserHelper from "../../../helpers/BrowserHelper";

interface Props {
  data: ChartData<"bar">;
  options: ChartOptions<"bar">;
  setChart?: (chart: Chart) => void;
}

const BarChart = (props: Props) => {
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = React.useRef<Chart | null>(null);
  const printMediaQueryListRef = React.useRef<MediaQueryList | null>(null);
  const prevPropsRef = React.useRef<Props>(props);

  const handleBeforePrint = React.useCallback((e: MediaQueryListEvent) => {
    const chart = chartInstanceRef.current;
    if (!chart) {
      return;
    }
    if (BrowserHelper.isTargetBrowser(["firefox"])) {
      return;
    }

    if (e.matches) {
      chart.options.responsive = false;
      chart.canvas.style.width = "100%";
      chart.update();
    } else {
      chart.options.responsive = true;
      chart.update();
    }
  }, []);

  const datasetsChanged = React.useCallback((): boolean => {
    const chart = chartInstanceRef.current;
    if (chart && chart.data.datasets && props.data.datasets) {
      return chart.data.datasets.length !== props.data.datasets.length;
    }
    return false;
  }, [props.data.datasets]);

  const labelsChanged = React.useCallback((): boolean => {
    const chart = chartInstanceRef.current;
    if (chart && chart.data.labels && props.data.labels) {
      return chart.data.labels !== props.data.labels;
    }
    return false;
  }, [props.data.labels]);

  const optionsChanged = React.useCallback(
    (prevOptions: ChartOptions<"bar">): boolean => {
      const chart = chartInstanceRef.current;
      if (chart && props.options) {
        const prev = cloneDeep(prevOptions);
        const current = merge(prev, props.options);
        return !isEqual(prev, current);
      }
      return false;
    },
    [props.options],
  );

  React.useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const ctx = chartRef.current.getContext("2d");
    if (!ctx) {
      return;
    }
    Chart.register(...registerables);

    const chart = new Chart(ctx, {
      options: props.options,
      data: props.data,
      type: "bar",
    });
    chartInstanceRef.current = chart;

    if (props.setChart) {
      props.setChart(chart);
    }

    if (chart.canvas) {
      const printMediaQueryList = window.matchMedia("print");
      printMediaQueryListRef.current = printMediaQueryList;
      printMediaQueryList.addEventListener("change", handleBeforePrint);
    }

    return () => {
      if (chart && typeof chart.destroy === "function") {
        chart.destroy();
      }
      printMediaQueryListRef.current?.removeEventListener(
        "change",
        handleBeforePrint,
      );
    };
  }, []);

  React.useEffect(() => {
    const chart = chartInstanceRef.current;
    const prevProps = prevPropsRef.current;

    if (!chart) {
      return;
    }

    if (
      datasetsChanged() ||
      labelsChanged() ||
      optionsChanged(prevProps.options)
    ) {
      chart.options = props.options;
      chart.data = props.data;
      chart.update();
    }

    prevPropsRef.current = props;
  }, [props, datasetsChanged, labelsChanged, optionsChanged]);

  return <canvas ref={chartRef} />;
};

export default BarChart;
